import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import dashboardApi from '../../services/dashboardApi';
import { MapFeatureProperties, QotdCalendarItem } from '../../types/dashboard';
import DashboardQotdMap from './DashboardQotdMap';

const toDateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const nycTodayKey = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const WEEKDAY_HEADERS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
const DEFAULT_CALENDAR_QUESTION = 'Still love NYC?';
const DEFAULT_CALENDAR_NEGATIVE_OPTION = 'Nah';
const DEFAULT_CALENDAR_POSITIVE_OPTION = 'Yeah';
const DEFAULT_EDITOR_NEGATIVE_OPTION = 'Nah';
const DEFAULT_EDITOR_POSITIVE_OPTION = 'Yeah';

const DashboardQotdTab = () => {
  const [cursor, setCursor] = useState(() => new Date());
  const [items, setItems] = useState<QotdCalendarItem[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(nycTodayKey());
  const [loading, setLoading] = useState(false);
  const [editorQuestion, setEditorQuestion] = useState('');
  const [editorBad, setEditorBad] = useState(DEFAULT_EDITOR_NEGATIVE_OPTION);
  const [editorGood, setEditorGood] = useState(DEFAULT_EDITOR_POSITIVE_OPTION);
  const [saving, setSaving] = useState(false);
  const [details, setDetails] = useState<any | null>(null);
  const [expandOpen, setExpandOpen] = useState(false);
  const [qotdLayer, setQotdLayer] = useState<'sentiment' | 'density'>('sentiment');
  const [selectedMapArea, setSelectedMapArea] = useState<MapFeatureProperties | null>(null);

  const today = nycTodayKey();
  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1;
  const firstDay = new Date(year, cursor.getMonth(), 1);
  const totalDays = new Date(year, cursor.getMonth() + 1, 0).getDate();
  const startWeekday = firstDay.getDay();
  const byDate = useMemo(() => new Map(items.map((item) => [item.date, item])), [items]);
  const selectedItem = byDate.get(selectedDate) || null;
  const isFuture = selectedDate > today;
  const isPastOrToday = selectedDate <= today;
  const monthLabel = MONTH_FORMATTER.format(firstDay);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardApi.getQotdCalendar({ year, month });
      setItems(response.items || []);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!document.hidden) {
        reload();
        if (selectedDate <= nycTodayKey()) {
          dashboardApi.getQotdResults(selectedDate).then(setDetails).catch(() => setDetails(null));
        }
      }
    }, 30000);
    return () => window.clearInterval(timer);
  }, [reload, selectedDate]);

  useEffect(() => {
    const onFocus = () => {
      reload();
      if (selectedDate <= nycTodayKey()) {
        dashboardApi.getQotdResults(selectedDate).then(setDetails).catch(() => setDetails(null));
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [reload, selectedDate]);

  useEffect(() => {
    if (!selectedItem) {
      setEditorQuestion(DEFAULT_CALENDAR_QUESTION);
      setEditorBad(DEFAULT_EDITOR_NEGATIVE_OPTION);
      setEditorGood(DEFAULT_EDITOR_POSITIVE_OPTION);
      return;
    }
    setEditorQuestion(selectedItem.question_text || DEFAULT_CALENDAR_QUESTION);
    setEditorBad(selectedItem.option_negative_text || DEFAULT_EDITOR_NEGATIVE_OPTION);
    setEditorGood(selectedItem.option_positive_text || DEFAULT_EDITOR_POSITIVE_OPTION);
  }, [selectedItem, selectedDate]);

  useEffect(() => {
    if (!isPastOrToday) return;
    dashboardApi.getQotdResults(selectedDate).then(setDetails).catch(() => setDetails(null));
  }, [selectedDate, isPastOrToday]);

  const saveQuestion = async () => {
    setSaving(true);
    try {
      await dashboardApi.saveQotdQuestion(selectedDate, {
        question_text: editorQuestion.trim(),
        option_negative_text: editorBad.trim(),
        option_positive_text: editorGood.trim(),
      });
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestion = async () => {
    setSaving(true);
    try {
      await dashboardApi.deleteQotdQuestion(selectedDate);
      await reload();
      setEditorQuestion(DEFAULT_CALENDAR_QUESTION);
      setEditorBad(DEFAULT_EDITOR_NEGATIVE_OPTION);
      setEditorGood(DEFAULT_EDITOR_POSITIVE_OPTION);
    } finally {
      setSaving(false);
    }
  };

  const openExpand = async () => {
    setExpandOpen(true);
  };

  const qotdAlertMessages = useMemo(() => {
    if (!selectedMapArea) return ['No alerts in the selected range.'];
    const messages: string[] = [];
    if (Number(selectedMapArea.sentiment_index || 0) <= -10) {
      messages.push('Negative pressure area detected (SI below -10).');
    }
    if (Number(selectedMapArea.total_votes || 0) >= 200) {
      messages.push('Vote spike detected in this area.');
    }
    return messages.length ? messages : ['No alerts in the selected range.'];
  }, [selectedMapArea]);

  const renderMapSidePanel = () => (
    <Stack spacing={2}>
      <Paper className="dashboard-section-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Layer Controls
        </Typography>
        <ToggleButtonGroup
          className="dashboard-layer-toggle"
          value={qotdLayer}
          exclusive
          onChange={(_, value) => value && setQotdLayer(value)}
          size="small"
          fullWidth
        >
          <ToggleButton value="sentiment">Sentiment</ToggleButton>
          <ToggleButton value="density">Vote Density</ToggleButton>
        </ToggleButtonGroup>
      </Paper>
      <Paper className="dashboard-section-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Selected Area Details
        </Typography>
        {selectedMapArea ? (
          <Stack spacing={0.6}>
            <Typography variant="subtitle1">{selectedMapArea.name}</Typography>
            <Typography className="dashboard-selected-sentiment">
              Sentiment Index: {Number(selectedMapArea.sentiment_index || 0).toFixed(1)}
            </Typography>
            <Typography className="dashboard-selected-votes-main">
              {Number(selectedMapArea.total_votes || 0)} votes
            </Typography>
            <Typography className="dashboard-selected-votes-ratio">
              <span className="dashboard-ratio-strong">{Number(selectedMapArea.positive_percentage || 0).toFixed(1)}%</span> positive ·{' '}
              <span className="dashboard-ratio-strong">
                {(100 - Number(selectedMapArea.positive_percentage || 0)).toFixed(1)}%
              </span>{' '}
              negative
            </Typography>
            {typeof selectedMapArea.density_percent === 'number' && (
              <Typography variant="body2">
                Vote density: <span className="dashboard-ratio-strong">{selectedMapArea.density_percent.toFixed(1)}%</span>
              </Typography>
            )}
            <Chip
              className="dashboard-confidence-chip"
              size="small"
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
              label={Number(selectedMapArea.total_votes || 0) >= 30 ? 'high confidence' : 'limited data'}
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click any area on the map to inspect details.
          </Typography>
        )}
      </Paper>
      <Paper className="dashboard-section-card" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Basic Alerts
        </Typography>
        <Stack spacing={0.8}>
          {qotdAlertMessages.map((message) => (
            <Typography key={message} variant="body2">
              {message}
            </Typography>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= totalDays; d += 1) {
    cells.push(new Date(year, cursor.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Paper className="dashboard-section-card dashboard-qotd-card" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6">Question of the Day</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setCursor(new Date(year, cursor.getMonth() - 1, 1))}>
            Prev month
          </Button>
          <Button variant="outlined" onClick={() => setCursor(new Date(year, cursor.getMonth() + 1, 1))}>
            Next month
          </Button>
        </Stack>
      </Stack>

      <Box className="dashboard-qotd-month-row">
        <Typography variant="h6">{monthLabel}</Typography>
      </Box>

      <Box className="dashboard-qotd-weekdays">
        {WEEKDAY_HEADERS.map((name) => (
          <span key={name}>{name}</span>
        ))}
      </Box>

      <Box className="dashboard-qotd-grid">
        {cells.map((cell, idx) => {
          const key = cell ? toDateKey(cell) : `empty-${idx}`;
          const item = cell ? byDate.get(toDateKey(cell)) : null;
          const isToday = cell ? toDateKey(cell) === today : false;
          const isSelected = cell ? toDateKey(cell) === selectedDate : false;
          const isCellFuture = cell ? toDateKey(cell) > today : false;
          const totalVotes = item?.total_votes || 0;
          const positivePct = Number(item?.positive_percentage || 0);
          const negativePct = Number(item?.negative_percentage || 0);

          return (
            <button
              type="button"
              key={key}
              className={[
                'dashboard-qotd-cell',
                !cell ? 'dashboard-qotd-cell--empty' : '',
                isSelected ? 'dashboard-qotd-cell--selected' : '',
                isToday ? 'dashboard-qotd-cell--today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => cell && setSelectedDate(toDateKey(cell))}
              disabled={!cell}
            >
              {cell ? (
                <>
                  <div className="dashboard-qotd-cell-top">
                    <span className="dashboard-qotd-day">{cell.getDate()}</span>
                    {item ? <span className="dashboard-qotd-votes">{totalVotes} votes</span> : null}
                  </div>
                  <p className="dashboard-qotd-question">{item?.question_text || DEFAULT_CALENDAR_QUESTION}</p>
                  {item ? (
                    isCellFuture ? (
                      <div className="dashboard-qotd-future-options">
                        <p>{item.option_positive_text || DEFAULT_CALENDAR_POSITIVE_OPTION}</p>
                        <p>{item.option_negative_text || DEFAULT_CALENDAR_NEGATIVE_OPTION}</p>
                      </div>
                    ) : (
                      <div className="dashboard-qotd-bars">
                        <div className="dashboard-qotd-bar-row">
                          <span className="dashboard-qotd-label">{item.option_positive_text} {Math.round(positivePct)}%</span>
                          <div className="dashboard-qotd-bar-track">
                            <div className="dashboard-qotd-bar-fill dashboard-qotd-bar-fill--good" style={{ width: `${positivePct}%` }} />
                          </div>
                        </div>
                        <div className="dashboard-qotd-bar-row">
                          <span className="dashboard-qotd-label">{item.option_negative_text} {Math.round(negativePct)}%</span>
                          <div className="dashboard-qotd-bar-track">
                            <div className="dashboard-qotd-bar-fill dashboard-qotd-bar-fill--bad" style={{ width: `${negativePct}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  ) : null}
                  {isSelected && !isCellFuture && item ? (
                    <Box className="dashboard-qotd-cell-actions">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(event) => {
                          event.stopPropagation();
                          openExpand();
                        }}
                      >
                        Expand
                      </Button>
                    </Box>
                  ) : null}
                </>
              ) : null}
            </button>
          );
        })}
      </Box>

      {loading ? <Typography variant="body2">Loading...</Typography> : null}

      {isFuture ? (
        <Stack spacing={1.2} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Future date editor ({selectedDate})</Typography>
          <TextField value={editorQuestion} onChange={(e) => setEditorQuestion(e.target.value)} label="Question" fullWidth />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField value={editorGood} onChange={(e) => setEditorGood(e.target.value)} label="Positive option" fullWidth />
            <TextField value={editorBad} onChange={(e) => setEditorBad(e.target.value)} label="Negative option" fullWidth />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={saveQuestion} disabled={!editorQuestion.trim() || saving}>
              Save
            </Button>
            <Button variant="outlined" color="error" onClick={deleteQuestion} disabled={saving}>
              Delete
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Question ({selectedDate})</Typography>
          <Typography variant="body2">{details?.question_text || selectedItem?.question_text || 'No question.'}</Typography>
          <Typography variant="body2">
            {details?.results?.total_votes || 0} votes · {Number(details?.results?.positive_percentage || 0).toFixed(1)}% positive ·{' '}
            {Number(details?.results?.negative_percentage || 0).toFixed(1)}% negative
          </Typography>
        </Stack>
      )}

      <Dialog open={expandOpen} onClose={() => setExpandOpen(false)} fullScreen PaperProps={{ className: 'dashboard-map-modal-paper' }}>
        <Box className="dashboard-map-modal-close-row">
          <Typography variant="h6" sx={{ mr: 'auto', ml: 0.5, color: '#e8edf4' }}>
            {selectedDate} · {details?.question_text || selectedItem?.question_text || 'Question of the Day'}
          </Typography>
          <IconButton className="dashboard-map-modal-close-button" onClick={() => setExpandOpen(false)} aria-label="Close map modal">
            <span className="material-symbols-outlined">close</span>
          </IconButton>
        </Box>
        <DialogContent className="dashboard-map-modal-content">
          <Box className="dashboard-map-modal-layout">
            <Box className="dashboard-map-modal-map-col">
              <DashboardQotdMap
                date={selectedDate}
                layer={qotdLayer}
                onAreaSelected={(feature) => setSelectedMapArea(feature)}
                fillHeight
              />
            </Box>
            <Box className="dashboard-map-modal-side-col">{renderMapSidePanel()}</Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default DashboardQotdTab;
